"use client";

import React from "react";
import { useAtomValue } from "jotai";
import { ChevronUp } from "lucide-react";
import { useWatch } from "react-hook-form";

import { RadioGroup } from "@/components/ui/radio-group";
import { Heading, Paragraph } from "@/components/ui/typography";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form";

import CoursePackage, {
  FeatureItem,
  CoursePackageMobile,
} from "@/components/course-package";

import CourseSection from "./course-section";

import useMediaQuery from "@/hooks/use-media-query";

import useMoengageEventTracker from "@/hooks/use-moengage";
import EVENT_NAME from "@/hooks/use-moengage/event-name";
import EVENT_ATTRIBUTE from "@/hooks/use-moengage/event-attribute";

import { coursePackagesAtom } from "@/store/course";

import { cn } from "@/lib/utils";

function CoursePackages({ className }) {
  const trackMoengageEvent = useMoengageEventTracker();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const packages = useAtomValue(coursePackagesAtom);

  const [collapsed, setCollapsed] = React.useState(true);
  const packageId = useWatch({ name: "packageId" });

  const currentPackage = packages.find((pkg) => packageId === pkg.packageId);

  React.useEffect(() => {
    if (!currentPackage) return;

    trackMoengageEvent(EVENT_NAME.PACKAGE_TAPPED, {
      [EVENT_ATTRIBUTE.COURSE_ID]: currentPackage.courseId,
      [EVENT_ATTRIBUTE.PACKAGE_NAME]: currentPackage.packageName,
      [EVENT_ATTRIBUTE.LANGUAGE_ID]: currentPackage.languageId,
    });
  }, [currentPackage, trackMoengageEvent]);

  if (!packages.length) return null;

  if (isMobile) {
    return (
      <CourseSection
        id="course-packages"
        title="Our Packages"
        cardContentClassName={cn("flex flex-col gap-y-5", className)}
      >
        <FormField
          name="packageId"
          render={({ field }) => (
            <RadioGroup
              className="grid grid-cols-3"
              onValueChange={field.onChange}
              defaultValue={field.value}
              {...field}
            >
              {packages.map((coursePackage) => (
                <CoursePackageMobile
                  key={coursePackage.packageId}
                  variant={coursePackage.packageName.toLowerCase()}
                  coursePackage={coursePackage}
                />
              ))}
            </RadioGroup>
          )}
        />

        {currentPackage && (
          <div className="flex flex-col gap-y-4 p-4 border border-primary-bg rounded-xl">
            <Heading variant="h5" weight="bold">
              Features
            </Heading>

            <div className="flex flex-col gap-y-4">
              {currentPackage.features.map((feature) => (
                <FeatureItem
                  key={feature.featureId}
                  feature={feature.featureName}
                  checked={!!feature.status}
                />
              ))}
            </div>
          </div>
        )}
      </CourseSection>
    );
  }

  return (
    <CourseSection
      id="course-packages"
      title="Our Packages"
      className={cn("flex flex-col gap-y-8", className)}
      action={
        <Button
          variant="link"
          className="no-underline!"
          onClick={() => setCollapsed((prev) => !prev)}
        >
          <Paragraph variant="b4" weight="sb" className="text-inherit">
            {collapsed ? "Show" : "Hide"} Details
          </Paragraph>

          <ChevronUp className={cn(collapsed && "-rotate-180")} />
        </Button>
      }
    >
      <FormField
        name="packageId"
        render={({ field }) => (
          <RadioGroup
            name="packageId"
            className="group grid grid-cols-3"
            onValueChange={field.onChange}
            defaultValue={field.value}
            {...field}
          >
            {packages.map((coursePackage) => (
              <CoursePackage
                key={coursePackage.packageId}
                variant={coursePackage.packageName.toLowerCase()}
                coursePackage={coursePackage}
                collapsed={collapsed}
                disabled={field.disabled}
              />
            ))}
          </RadioGroup>
        )}
      />
    </CourseSection>
  );
}

export default CoursePackages;
